import * as React from 'react';
import {
  ChakraProvider,
  EnvironmentProvider,
  defaultSystem,
} from '@chakra-ui/react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { createPortal } from 'react-dom';

function memoize<T extends object, R>(func: (arg: T) => R): (arg: T) => R {
  const cache = new WeakMap<T, R>();
  return (arg: T) => {
    if (cache.has(arg)) return cache.get(arg)!;
    const ret = func(arg);
    cache.set(arg, ret);
    return ret;
  };
}

const createCacheFn = memoize((container: HTMLElement) =>
  createCache({ container, key: 'frame' }),
);

/**
 * IframeProvider - Provides React context inside an iframe.
 * Note: This component requires manual iframe creation and portal rendering.
 * For simple PDF viewing, use native <iframe> instead.
 *
 * @deprecated Use native iframe for PDF viewing. For React content in iframes,
 * create a custom solution using createPortal.
 */
export const IframeProvider = (props: React.PropsWithChildren) => {
  console.warn(
    'IframeProvider is deprecated. Use native iframe for PDF viewing or createPortal for React content.',
  );
  return <>{props.children}</>;
};

export interface EnvironmentIframeProps extends React.IframeHTMLAttributes<HTMLIFrameElement> {
  /** Use EnvironmentProvider wrapper for React content inside iframe */
  useEnvironmentProvider?: boolean;
}

/**
 * Reusable iframe component with optional EnvironmentProvider support.
 * Drop-in replacement for <iframe>. Set useEnvironmentProvider={true} for React content.
 */
export const EnvironmentIframe = React.forwardRef<
  HTMLIFrameElement,
  EnvironmentIframeProps & { children?: React.ReactNode }
>(
  (
    {
      useEnvironmentProvider = false,
      src,
      title,
      width,
      height,
      style,
      className,
      allowFullScreen,
      loading,
      onLoad,
      onError,
      children,
      ...rest
    },
    ref,
  ) => {
    // For simple iframe usage (like PDF viewing), just render a standard iframe
    if (!useEnvironmentProvider) {
      return (
        <iframe
          ref={ref}
          src={src}
          title={title}
          width={width}
          height={height}
          style={style}
          className={className}
          allowFullScreen={allowFullScreen}
          loading={loading}
          onLoad={onLoad}
          onError={onError}
          {...rest}
        />
      );
    }

    // For React content inside iframe, use React Portal
    // Note: This requires the iframe to be in the same origin or CORS-enabled
    const [iframeRef, setIframeRef] = React.useState<HTMLIFrameElement | null>(
      null,
    );
    const [iframeDocument, setIframeDocument] = React.useState<Document | null>(
      null,
    );

    React.useEffect(() => {
      if (iframeRef?.contentDocument) {
        setIframeDocument(iframeRef.contentDocument);
      }
    }, [iframeRef]);

    return (
      <>
        <iframe
          ref={(node) => {
            setIframeRef(node);
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              (
                ref as React.MutableRefObject<HTMLIFrameElement | null>
              ).current = node;
            }
          }}
          src={src}
          title={title}
          width={width}
          height={height}
          style={{
            width: width || '100%',
            height: height || '100%',
            border: 'none',
            ...style,
          }}
          className={className}
          allowFullScreen={allowFullScreen}
          loading={loading}
          onLoad={onLoad}
          onError={onError}
          {...rest}
        />
        {iframeDocument &&
          children &&
          createPortal(
            <CacheProvider value={createCacheFn(iframeDocument.head)}>
              <EnvironmentProvider value={() => iframeDocument}>
                <ChakraProvider value={defaultSystem}>
                  {children}
                </ChakraProvider>
              </EnvironmentProvider>
            </CacheProvider>,
            iframeDocument.body,
          )}
      </>
    );
  },
);

EnvironmentIframe.displayName = 'EnvironmentIframe';
